import React, { FC, useEffect, useRef, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"

import { SiteNavQueryQuery } from "@graphqlTypes"

import { generateConditionalClassName, isClientSide } from "@utils"
import { BREAKPOINTS, LOCAL_STORAGE_NAME_LOGGED_IN } from "@constants"

import useCommonUrlsAndNumbers from "@hooks/useCommonUrlsAndNumbers"

import Icon, { ICON_TYPES } from "@components/Icon/Icon"
import SiteNavLogo from "./SiteNavLogo/SiteNavLogo"
import Button from "@components/Button/Button"

import { useScrolledAndVisible } from "./SiteNav.hooks"

import "./SiteNav.scss"
import AnchorLink from "@components/AnchorLink/AnchorLink"

const SiteNav: FC<{
  includeLoginButton?: boolean
  isStatic?: boolean
}> = ({
  includeLoginButton,
  isStatic,
}) => {
    const { siteNavData } = useStaticQuery<SiteNavQueryQuery>(graphql`
      query SiteNavQuery {
        siteNavData: contentfulJsonData(contentful_id: {eq: "3YkxWPwJ7xkjNbrYfNNVTn"}) {
          json {
            navItems {
              title
              url
              navItems {
                title
                description
                icon
                url
              }
              feature {
                overline
                title
                imageId
                image {
                  images {
                    fallback {
                      src
                      srcSet
                      sizes
                    }
                  }
                  layout
                  width
                  height
                }
                url
              }
            }
          }
        }
      }
    `)

    const navItems = siteNavData?.json?.navItems

    if (!navItems) return null

    const {
      demoUrl,
      signupPlusUrl,
    } = useCommonUrlsAndNumbers()

    const elRef = useRef<HTMLElement>(null)
    const [isReady, setIsReady] = useState<boolean>(true)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [expandedNavLink, setExpandedNavLink] = useState<string>("")
    const scrolledAndVisibleState = useScrolledAndVisible(isOpen)

    const handleClickLink = () => {
      setIsOpen(false)
      setExpandedNavLink("")

      // Trick any open nav items into closing while navigating to a new page
      setIsReady(false)
      setTimeout(() => setIsReady(true), 300)
    }

    const handleClickLogin = () => {
      window.localStorage.setItem(LOCAL_STORAGE_NAME_LOGGED_IN, "true")
    }

    useEffect(() => {
      if (!isClientSide() || window.innerWidth >= BREAKPOINTS.M || !elRef.current) return

      const navDrawers = Array.from(elRef.current.querySelectorAll<HTMLUListElement>(".nav-drawer"))
      navDrawers.forEach(navDrawer => {
        const _transition = navDrawer.style.transition
        navDrawer.style.transition = ""
        navDrawer.style.maxHeight = "none"
        const { height } = navDrawer.getBoundingClientRect()
        navDrawer.style.setProperty("--SiteNav--nav-drawer-max-height", `${height}px`)
        navDrawer.style.maxHeight = ""
        navDrawer.style.transition = _transition
      })
    }, [])

    useEffect(() => {
      if (!isClientSide() || window.innerWidth >= BREAKPOINTS.M || !isOpen) return

      const handleClickOutside = (e: MouseEvent | TouchEvent) => {
        const closestNav = e.target?.closest(".main-nav, .touch-menu-toggle")
        if (closestNav) return
        setIsOpen(false)
      }

      window.addEventListener("click", handleClickOutside)

      return () => {
        window.removeEventListener("click", handleClickOutside)
      }
    }, [isOpen])

    return (
      <nav
        className={generateConditionalClassName({
          "SiteNav": true,
          "is-open": isOpen,
          "is-ready": isReady,
          "is-static": !!isStatic,
          [`scrolled--${scrolledAndVisibleState}`]: !isStatic && !!scrolledAndVisibleState,
        })}
        ref={elRef}
      >
        <div className="container">
          <div className="nav-pill flex">
            <a className="skip-to-content" href="#main-content">Skip to content</a>
            <AnchorLink
              aria-label="Return to home page"
              className="nav-logo"
              href="/"
            >
              <SiteNavLogo />
            </AnchorLink>
            <ul className="main-nav">
              {navItems.map(navItem => !navItem ? null : (
                <li key={navItem.title}>
                  {navItem.url ? (
                    <AnchorLink
                      className="nav-link"
                      onClick={handleClickLink}
                      href={navItem.url}
                    >
                      {navItem.title}
                    </AnchorLink>
                  ) : (
                    <button
                      className={generateConditionalClassName({
                        "nav-link has-drawer": true,
                        "is-expanded": expandedNavLink === navItem.title
                      })}
                      onClick={() => {
                        if (window.innerWidth > BREAKPOINTS.M) return

                        setExpandedNavLink(_expandedNavLink => {
                          if (window.innerWidth >= BREAKPOINTS.M) return ""
                          return (_expandedNavLink && _expandedNavLink === navItem.title) ?
                            "" :
                            (navItem.title || "")
                        })
                      }}
                    >
                      {navItem.title}
                      {" "}
                      <Icon
                        size="16px"
                        type={ICON_TYPES["chevron-down"]}
                      />
                    </button>
                  )}
                  {isReady && navItem.navItems && (
                    <ul
                      className={generateConditionalClassName({
                        "nav-drawer": true,
                        "has-feature": !!navItem.feature,
                      })}
                    >
                      {navItem.navItems.map(subNavItem => !subNavItem ? null : (
                        <li key={subNavItem.title}>
                          <AnchorLink
                            className="subnav-link"
                            onClick={handleClickLink}
                            href={subNavItem.url || "/"}
                          >
                            <span className="title font-family--sans-brand">
                              {subNavItem.icon && (
                                <Icon size="1.5em" type={subNavItem.icon as ICON_TYPES} />
                              )}
                              {subNavItem.title}
                            </span>
                            {subNavItem.description && (
                              <span className="description">{subNavItem.description}</span>
                            )}
                          </AnchorLink>
                        </li>
                      ))}
                      {navItem.feature && (
                        <li className="feature">
                          <AnchorLink
                            onClick={handleClickLink}
                            href={navItem.feature.url || "/"}
                          >
                            <span className="type--mono-XS color--clay-800 margin-bottom--base-0-75">{navItem.feature.overline}</span>
                            <GatsbyImage
                              alt={navItem.feature.title || "Resource image"}
                              className="border-radius--M margin-bottom--base-1"
                              image={navItem.feature.image as IGatsbyImageData}
                            />
                            <span
                              className="type--headline-medium-XS color--text-headline margin-bottom--0"
                              dangerouslySetInnerHTML={{ __html: navItem.feature.title || "" }}
                            />
                          </AnchorLink>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              ))}
              <li className="get-a-demo-or-login">
                {includeLoginButton && (
                  <AnchorLink
                    className="nav-link font-family--sans-brand"
                    href="https://secure.helpscout.net/"
                    onClick={handleClickLogin}
                  >
                    Login
                  </AnchorLink>
                )}
                {!includeLoginButton && (
                  <AnchorLink
                    className="nav-link get-a-demo font-family--sans-brand"
                    onClick={handleClickLink}
                    href={demoUrl || "/"}
                  >
                    Get a Demo
                  </AnchorLink>
                )}
              </li>
              <li className="free-trial">
                <Button href={signupPlusUrl || "/"}>
                  <>Start<span className="hide-tablet"> for</span> Free</>
                </Button>
              </li>
            </ul>
            <div className="touch-menu-toggle">
              <button
                aria-label={isOpen ? "Close Menu" : "Open Menu"}
                onClick={() => { setIsOpen(_isOpen => !_isOpen) }}
              >
                <Icon
                  size="24px"
                  type={ICON_TYPES["close"]}
                />
                <Icon
                  size="24px"
                  type={ICON_TYPES["menu"]}
                />
              </button>
            </div>
          </div>
        </div>
      </nav>
    )
  }

export default SiteNav
